import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { trans } from 'sharedKernel/i18n'
import { HubTable } from 'components/HubDefaultTable'
import { NoContent } from 'components/NoContent'
import NoContentImg from 'assets/images/defaults/no_challenges.png'
import { ApproveRejectButtons } from 'components/ApproveRejectButtons'
import { TitleContainer } from 'components/HubDefaultTable/style'
import { useParams } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { AnimatedSortButtons } from 'components/HubDefaultTable/OrderByButton'
import { CenterContent, CellLink, IdeaTitle } from '../table/style'
import { filterValueMatchAnyField } from '../table/behavior'
import { HeaderCell, CompactHeaderCell } from './style'
import { ISolution } from '../contracts'
import { EVALUATION_STATUS, SOLUTION_STATUS } from 'sharedKernel/constants'
import { formatBooleanEvaluationValue, getBooleanCriterionRawValue } from '../formatBooleanEvaluationValue'

export interface SpecialComplianceTableProps {
  solutions: Solutions
  filterValue: string
  approveSolution: (solutionId: string) => void
  archiveSolution: (solutionId: string) => void
  stepData?: any
}

interface Solutions {
  rows: any[]
}

const NO_CONTENT_MESSAGE = 'Nenhuma solução encontrada para este evento'

export const SpecialComplianceTable = ({ solutions, filterValue, approveSolution, archiveSolution, stepData }: SpecialComplianceTableProps): JSX.Element => {
  const { challengeId } = useParams()
  const navigate = useNavigate()
  const [sortAsc, setSortAsc] = useState(false)

  const labels = {
    solution: trans('Solução'),
    completeDocumentation: trans('Documentação completa'),
    passiveSolution: trans('Solução passível'),
    legalSolution: trans('Solução legal'),
    compatibleSolution: trans('Solução compatível com os processos legais da caixa'),
    annexTI: trans('ANEXO TI - Análise de Requisitos (SUART)'),
    situation: trans('Situação'),
    actions: trans('Ações'),
    noContent: trans(NO_CONTENT_MESSAGE),
    yes: trans('SIM'),
    no: trans('NÃO'),
    pending: trans('Pendente'),
    approved: trans('Aprovado'),
    disapproved: trans('Reprovado'),
    alreadyEvaluated: trans('Esta solução já foi aprovada ou rejeitada.')
  }

  const getCriteria = (): any[] => {
    if (!stepData || !stepData.sections) return []
    const allFields: any[] = []
    stepData.sections.forEach((section: any) => {
      if (section.fields) {
        allFields.push(...section.fields)
      }
    })
    return allFields
  }

  const criteria = getCriteria()

  const capitalizeFirstLetter = (text: string): string => {
    if (!text) return text
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  const tableHeader = (): any[] => {
    const headers = [getCenteredContent(buildHeaderSolutionTitle())]
    
    criteria.forEach((criterion: any) => {
      const columnName = capitalizeFirstLetter(criterion.name || '')
      headers.push(getCenteredContent(<CompactHeaderCell>{columnName}</CompactHeaderCell>))
    })
    
    headers.push(getCenteredContent(<CompactHeaderCell>{labels.situation}</CompactHeaderCell>))
    headers.push(getCenteredContent(<CompactHeaderCell>{labels.actions}</CompactHeaderCell>))
    
    return headers
  }

  const buildHeaderSolutionTitle = (): JSX.Element => {
    return (
      <CompactHeaderCell>
        {labels.solution}
        <div className='ml-1'>
          <AnimatedSortButtons 
            orderByAsc={sortAsc}
            onClick={() => setSortAsc(!sortAsc)} 
          />
        </div>
      </CompactHeaderCell>
    )
  }

  const buildSolutionRows = (): any[] => {
    if (!solutions || !solutions.rows) return []
    
    return solutions.rows
      .filter((solution) => filterValueMatchAnyField(solution, filterValue, {}))
      .map((solution: any) => buildSolutionColumns(solution))
  }

  const buildSolutionColumns = (solution: any): any[] => {
    const columns: any = []

    columns.push(getSolutionTitle(solution))
    
    criteria.forEach((criterion: any) => {
      const value = getCriterionValue(solution, criterion)
      columns.push(getCenteredContent(value))
    })
    
    columns.push(getCenteredContent(getSolutionStatus(solution)))
    columns.push(getSolutionActions(solution))

    return columns
  }

  const getCriterionValue = (solution: any, criterion: any): string => {
    const criterionId = criterion?.id || criterion?._id

    if (!criterionId) {
      return '-'
    }

    const rawValue = getBooleanCriterionRawValue(solution, criterionId)

    return formatBooleanEvaluationValue(rawValue, {
      yes: labels.yes,
      no: labels.no,
    })
  }

  const getSolutionTitle = (solution: any): JSX.Element => {
    const title = solution.idea?.name || solution.title || solution.ideaTitle || solution.name || 'Sem título'
    const solutionId = solution.idea?.id || solution.id || solution.ideaId
    
    return (
      <TitleContainer className='d-flex justify-content-start'>
        <CellLink
          onClick={() => navigate(`/hub/company/challenge/${challengeId}/solutions/${solutionId}/subscription`)}
        >
          <IdeaTitle>
            {title}
          </IdeaTitle>
        </CellLink>
      </TitleContainer>
    )
  }

  const getSolutionStatus = (solution: any): JSX.Element => {
    const statusConfig = {
      APPROVED: { bgColor: '#d4edda', color: '#155724', label: labels.approved },
      REJECTED: { bgColor: '#f8d7da', color: '#721c24', label: labels.disapproved },
      CREATED: { bgColor: '#fff3cd', color: '#856404', label: labels.pending },
      LAUNCHED: { bgColor: '#fff3cd', color: '#856404', label: labels.pending }
    }

    const statusName = solution.status ? solution.status.toUpperCase() : 'CREATED'
    const status = statusConfig[statusName];
    
    if (!status)
    {
      throw new Error(`Status not found ${statusName}`)
    }
    
    return (
      <div 
        style={{
          padding: '4px 12px',
          borderRadius: '4px',
          backgroundColor: status.bgColor,
          color: status.color,
          fontWeight: 500,
          display: 'inline-block'
        }}
      >
        {status.label}
      </div>
    )
  }

  const getSolutionActions_old = (solution: any): JSX.Element => {
   
    const ideaEvaluationStepId = solution.ideaEvaluationStepId
    return (
      <CenterContent>
        <ApproveRejectButtons
          positiveAction={() => approveSolution(ideaEvaluationStepId)}
          negativeAction={() => archiveSolution(ideaEvaluationStepId)}
          disabled={false}
          tipyContent={''}
        />
      </CenterContent>
    )
  }
  const normalizeStatus = (status: any): string =>  status? status.toUpperCase() : 'CREATED'

  const getSolutionActions = (solution: any): JSX.Element => {
    const statusName = normalizeStatus(solution.status);
    
    const getTippyMessage = (): string => {
      return canExecuteAction()? "": labels.alreadyEvaluated
    }

    const getIdeaEvaluationStepId = (): string => {
      if (solution.id) {
        return solution.id
      }
      throw new Error("ideaEvaluationStepId  not exists.")
    }

    const canExecuteAction  =  (): boolean => {
      return statusName === normalizeStatus(SOLUTION_STATUS.LAUNCHED)  ||  
        statusName === normalizeStatus(SOLUTION_STATUS.CREATED)
    }

    return (
      <CenterContent>
        <ApproveRejectButtons
          positiveAction={() => approveSolution(getIdeaEvaluationStepId())}
          negativeAction={() => archiveSolution(getIdeaEvaluationStepId())}
          disabled={!canExecuteAction()}
          tipyContent={getTippyMessage()}
        />
      </CenterContent>
    )
  }  

  const getCenteredContent = (content: any): any => {
    return (
      <CenterContent>
        {content}
      </CenterContent>
    )
  }

  return (
    <>
      {
        !solutions || !solutions.rows || solutions.rows.length === 0
          ? <NoContent text={labels.noContent} image={NoContentImg} />
          : <div style={{ 
              width: '100%', 
              height: 'calc(100vh - 300px)', 
              overflow: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}>
              <HubTable
                header={tableHeader()}
                rows={buildSolutionRows()}
              />
            </div>
      }
    </>
  )
}
